import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  FiPlus, FiTrash2, FiEye, FiEyeOff, FiAward, FiSearch,
  FiEdit2, FiPlusCircle, FiMinusCircle, FiUpload, FiDownload,
  FiFileText, FiAlertCircle, FiCheckCircle, FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { resultAPI } from '../../services/api';
import { calculateGrade } from '../../utils/helpers';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/common/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const EXAM_TYPES = ['First Terminal', 'Second Terminal', 'Final Examination', 'Pre-Board', 'Unit Test'];
const CLASSES = ['Nursery', 'KG', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8', 'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'];
const SECTIONS = ['A', 'B', 'C', 'D'];

const emptySubject = { name: '', fullMarks: 100, passMarks: 40, obtainedMarks: '' };
const emptyForm = { studentName: '', rollNumber: '', class: '', section: 'A', examType: 'Final Examination', academicYear: '2024-2025', subjects: [{ ...emptySubject }] };

const AdminResults = () => {
  const [results, setResults] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);

  const fetchResults = async () => {
    try {
      const res = await resultAPI.getAll({ limit: 100 });
      const data = res.data?.data?.results || [];
      setResults(data);
      setFiltered(data);
    } catch {
      setResults([]); setFiltered([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResults(); }, []);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    setFiltered(q ? results.filter((r) => r.studentName?.toLowerCase().includes(q) || r.rollNumber?.toLowerCase().includes(q)) : results);
  }, [search, results]);

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      studentName: item.studentName, rollNumber: item.rollNumber, class: item.class,
      section: item.section || 'A', examType: item.examType, academicYear: item.academicYear,
      subjects: item.subjects?.map((s) => ({ name: s.name, fullMarks: s.fullMarks, passMarks: s.passMarks, obtainedMarks: s.obtainedMarks })) || [{ ...emptySubject }]
    });
    setModal(true);
  };

  const addSubject = () => setForm((p) => ({ ...p, subjects: [...p.subjects, { ...emptySubject }] }));
  const removeSubject = (idx) => setForm((p) => ({ ...p, subjects: p.subjects.filter((_, i) => i !== idx) }));
  const updateSubject = (idx, field, value) => setForm((p) => ({
    ...p, subjects: p.subjects.map((s, i) => i === idx ? { ...s, [field]: value } : s)
  }));

  const handleSave = async () => {
    if (!form.studentName.trim() || !form.rollNumber.trim() || !form.class || !form.subjects.some((s) => s.name)) {
      toast.error('Please fill all required fields'); return;
    }
    setSaving(true);
    const payload = { ...form, subjects: form.subjects.filter((s) => s.name.trim()) };
    try {
      if (editItem) {
        const res = await resultAPI.update(editItem._id, payload);
        const updated = res.data?.data?.result || { ...editItem, ...payload };
        setResults((prev) => prev.map((r) => r._id === editItem._id ? updated : r));
        toast.success('Result updated');
      } else {
        const res = await resultAPI.create({ ...payload, isPublished: false });
        const newItem = res.data?.data?.result || { ...payload, _id: Date.now().toString(), isPublished: false };
        setResults((prev) => [newItem, ...prev]);
        toast.success('Result created');
      }
      setModal(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save result');
    } finally { setSaving(false); }
  };

  const handleToggle = async (id) => {
    try {
      await resultAPI.togglePublish(id);
      setResults((prev) => prev.map((r) => r._id === id ? { ...r, isPublished: !r.isPublished } : r));
      toast.success('Result status updated');
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    try {
      await resultAPI.delete(id);
      setResults((prev) => prev.filter((r) => r._id !== id));
      setDeleteConfirm(null);
      toast.success('Result deleted');
    } catch {
      toast.error('Failed to delete result');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await resultAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'results_template.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { setExcelFile(file); setUploadResult(null); }
  };

  const handleExcelUpload = async () => {
    if (!excelFile) { toast.error('Please select an Excel file'); return; }
    setUploading(true); setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', excelFile);
      const res = await resultAPI.uploadExcel(fd);
      const data = res.data;
      setUploadResult(data);
      if (data.success) { toast.success(data.message); fetchResults(); }
    } catch (err) {
      const errData = err?.response?.data;
      setUploadResult(errData || { success: false, message: 'Upload failed' });
      toast.error(errData?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  const getTotals = (subjects) => {
    const full = subjects?.reduce((s, sub) => s + (Number(sub.fullMarks) || 0), 0) || 0;
    const obtained = subjects?.reduce((s, sub) => s + (Number(sub.obtainedMarks) || 0), 0) || 0;
    const pct = full > 0 ? ((obtained / full) * 100).toFixed(1) : 0;
    return { full, obtained, pct };
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Results Management</h2>
          <p className="text-slate-500 text-sm mt-1">Add and publish student examination results</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={handleDownloadTemplate}
            className="flex items-center gap-2 px-4 py-2.5 border border-blue-100 text-slate-600 rounded-xl font-medium hover:bg-blue-50 transition-colors text-sm">
            <FiDownload className="w-4 h-4" /> Template
          </button>
          <button onClick={() => { setExcelFile(null); setUploadResult(null); setShowUploadModal(true); }}
            className="flex items-center gap-2 px-4 py-2.5 border border-blue-200 text-[#2563EB] rounded-xl font-medium hover:bg-blue-50 transition-colors text-sm">
            <FiUpload className="w-4 h-4" /> Upload Excel
          </button>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1D4ED8] text-white rounded-xl font-semibold hover:bg-[#1e3a8a] transition-colors text-sm shadow-md">
            <FiPlus className="w-4 h-4" /> Add Result
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 shadow-sm">
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input type="text" placeholder="Search by student name or roll number..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Student</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Class</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Exam</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden xl:table-cell">Score</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((result, idx) => {
                  const { full, obtained, pct } = getTotals(result.subjects);
                  const grade = calculateGrade(parseFloat(pct));
                  return (
                    <motion.tr key={result._id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.03 }}
                      className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-[#1e3a8a] text-sm">{result.studentName}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{result.rollNumber}</div>
                      </td>
                      <td className="px-5 py-4 hidden md:table-cell text-slate-500 text-xs">{result.class}{result.section ? ` - ${result.section}` : ''}</td>
                      <td className="px-5 py-4 hidden lg:table-cell text-slate-400 text-xs">{result.examType}</td>
                      <td className="px-5 py-4 hidden xl:table-cell">
                        <div className="text-xs flex items-center gap-2">
                          <span className="font-semibold text-[#1e3a8a]">{obtained}/{full}</span>
                          <span className="text-slate-400">({pct}%)</span>
                          <span className="px-1.5 py-0.5 rounded-full bg-blue-100 text-[#2563EB] font-semibold text-[10px]">{grade}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${result.isPublished ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                          {result.isPublished ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setViewModal(result)} className="p-1.5 text-slate-500 hover:text-[#1D4ED8] hover:bg-blue-50 rounded-lg transition-colors" title="View"><FiEye className="w-3.5 h-3.5" /></button>
                          <button onClick={() => openEdit(result)} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors" title="Edit"><FiEdit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => handleToggle(result._id)} className={`p-1.5 rounded-lg transition-colors ${result.isPublished ? 'text-amber-400 hover:bg-amber-50' : 'text-emerald-400 hover:bg-emerald-50'}`} title={result.isPublished ? 'Unpublish' : 'Publish'}>
                            {result.isPublished ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
                          </button>
                          <button onClick={() => setDeleteConfirm(result._id)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><FiTrash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-14">
              <FiAward className="w-10 h-10 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No results found</p>
            </div>
          )}
        </div>
      )}

      {/* Excel Upload Modal */}
      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Results via Excel" size="lg"
        footer={
          <>
            <button onClick={() => setShowUploadModal(false)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Close</button>
            <button onClick={handleExcelUpload} disabled={uploading || !excelFile}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a8a] disabled:opacity-70">
              {uploading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <FiUpload className="w-4 h-4" />}
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl p-4">
            <FiFileText className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-800">Download the Excel template first</p>
              <p className="text-xs text-blue-500 mt-0.5">Fill in student details with subject columns (up to 10 subjects per student).</p>
              <button onClick={handleDownloadTemplate} className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 font-semibold hover:underline">
                <FiDownload className="w-3.5 h-3.5" /> Download Template
              </button>
            </div>
          </div>
          <div onClick={() => fileInputRef.current?.click()}
            className="border-2 border-dashed border-blue-100 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors">
            <FiUpload className="w-8 h-8 text-blue-200 mx-auto mb-2" />
            {excelFile ? (
              <div>
                <p className="text-sm font-semibold text-[#1D4ED8]">{excelFile.name}</p>
                <p className="text-xs text-slate-400 mt-1">{(excelFile.size / 1024).toFixed(1)} KB</p>
              </div>
            ) : (
              <div>
                <p className="text-sm text-slate-400">Click to select Excel file (.xlsx)</p>
                <p className="text-xs text-slate-300 mt-1">Max 5 MB</p>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileChange} className="hidden" />
          </div>
          {uploadResult && (
            <div className={`rounded-xl border p-4 ${uploadResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                {uploadResult.success ? <FiCheckCircle className="w-4 h-4 text-emerald-600" /> : <FiAlertCircle className="w-4 h-4 text-red-600" />}
                <p className={`text-sm font-semibold ${uploadResult.success ? 'text-emerald-800' : 'text-red-800'}`}>{uploadResult.message}</p>
              </div>
              {uploadResult.success && uploadResult.data && (
                <div className="flex gap-4 text-xs mt-1">
                  <span className="text-emerald-700">Inserted: <b>{uploadResult.data.inserted}</b></span>
                  <span className="text-blue-700">Updated: <b>{uploadResult.data.updated}</b></span>
                  {uploadResult.data.failed > 0 && <span className="text-red-700">Failed: <b>{uploadResult.data.failed}</b></span>}
                </div>
              )}
              {uploadResult.data?.rowErrors?.length > 0 && (
                <div className="mt-3 max-h-40 overflow-y-auto space-y-1">
                  <p className="text-xs font-semibold text-red-700 mb-1">Row errors:</p>
                  {uploadResult.data.rowErrors.map((e, i) => (
                    <div key={i} className="text-xs text-red-600 bg-red-100 rounded-lg px-2 py-1">Row {e.row}: {e.errors.join(', ')}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title={editItem ? 'Edit Result' : 'Add Result'} size="xl"
        footer={
          <>
            <button onClick={() => setModal(false)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Cancel</button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a8a] disabled:opacity-70">
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {editItem ? 'Update' : 'Save'}
            </button>
          </>
        }
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Student Information</h3>
            <div className="grid grid-cols-2 gap-4">
              {[
                ['Student Name *', 'studentName', 'text', 'Full name'],
                ['Roll Number *', 'rollNumber', 'text', 'GA-2024-XXX'],
              ].map(([label, field, type, placeholder]) => (
                <div key={field}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</label>
                  <input value={form[field]} onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
                    placeholder={placeholder} type={type}
                    className="w-full px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Class *</label>
                <select value={form.class} onChange={(e) => setForm((p) => ({ ...p, class: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400">
                  <option value="">Select Class</option>
                  {CLASSES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Section</label>
                <select value={form.section} onChange={(e) => setForm((p) => ({ ...p, section: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400">
                  {SECTIONS.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Exam Type</label>
                <select value={form.examType} onChange={(e) => setForm((p) => ({ ...p, examType: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400">
                  {EXAM_TYPES.map((e) => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Academic Year</label>
                <input value={form.academicYear} onChange={(e) => setForm((p) => ({ ...p, academicYear: e.target.value }))}
                  placeholder="2024-2025"
                  className="w-full px-3 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 text-slate-700" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Subjects & Marks</h3>
              <button onClick={addSubject} className="flex items-center gap-1.5 text-[#1D4ED8] hover:text-[#1e3a8a] text-xs font-semibold transition-colors">
                <FiPlusCircle className="w-4 h-4" /> Add Subject
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-5 gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider px-2">
                <span className="col-span-2">Subject</span>
                <span>Full</span>
                <span>Pass</span>
                <span>Obtained</span>
              </div>
              {form.subjects.map((sub, idx) => (
                <div key={idx} className="grid grid-cols-5 gap-2 items-center">
                  <input value={sub.name} onChange={(e) => updateSubject(idx, 'name', e.target.value)} placeholder="Subject name"
                    className="col-span-2 px-3 py-2 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 text-slate-700" />
                  <input type="number" value={sub.fullMarks} onChange={(e) => updateSubject(idx, 'fullMarks', e.target.value)}
                    className="px-3 py-2 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400" />
                  <input type="number" value={sub.passMarks} onChange={(e) => updateSubject(idx, 'passMarks', e.target.value)}
                    className="px-3 py-2 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400" />
                  <div className="flex gap-1">
                    <input type="number" value={sub.obtainedMarks} onChange={(e) => updateSubject(idx, 'obtainedMarks', e.target.value)} placeholder="0"
                      className="flex-1 px-3 py-2 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400" />
                    {form.subjects.length > 1 && (
                      <button onClick={() => removeSubject(idx)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0">
                        <FiMinusCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal>

      {/* View Result Modal */}
      <Modal isOpen={!!viewModal} onClose={() => setViewModal(null)} title="Result Details" size="lg">
        {viewModal && (() => {
          const { full, obtained, pct } = getTotals(viewModal.subjects);
          const grade = calculateGrade(parseFloat(pct));
          return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-y-3 text-sm">
                {[
                  ['Student Name', viewModal.studentName],
                  ['Roll Number', viewModal.rollNumber],
                  ['Class', `${viewModal.class}${viewModal.section ? ` - ${viewModal.section}` : ''}`],
                  ['Exam Type', viewModal.examType],
                  ['Academic Year', viewModal.academicYear],
                ].map(([label, val]) => (
                  <div key={label}>
                    <p className="text-slate-400 text-xs mb-0.5">{label}</p>
                    <p className="font-semibold text-[#1e3a8a]">{val}</p>
                  </div>
                ))}
              </div>
              <div className="border-t border-blue-50 pt-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-50 rounded-xl">
                      <th className="text-left px-3 py-2 text-slate-500 text-xs font-semibold uppercase tracking-wider rounded-l-xl">Subject</th>
                      <th className="text-center px-3 py-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">Full</th>
                      <th className="text-center px-3 py-2 text-slate-500 text-xs font-semibold uppercase tracking-wider">Pass</th>
                      <th className="text-center px-3 py-2 text-slate-500 text-xs font-semibold uppercase tracking-wider rounded-r-xl">Obtained</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewModal.subjects?.map((sub, i) => (
                      <tr key={i} className="border-b border-blue-50">
                        <td className="px-3 py-2 text-slate-600">{sub.name}</td>
                        <td className="px-3 py-2 text-center text-slate-500">{sub.fullMarks}</td>
                        <td className="px-3 py-2 text-center text-slate-500">{sub.passMarks}</td>
                        <td className={`px-3 py-2 text-center font-semibold ${sub.obtainedMarks >= sub.passMarks ? 'text-[#1e3a8a]' : 'text-red-500'}`}>{sub.obtainedMarks}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-slate-400">Total</p>
                  <p className="font-bold text-[#1e3a8a]">{obtained}/{full}</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-sky-400">Percentage</p>
                  <p className="font-bold text-sky-600">{pct}%</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 text-center">
                  <p className="text-xs text-slate-400">Grade</p>
                  <p className="font-bold text-[#1D4ED8] text-lg">{grade}</p>
                </div>
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Confirm Delete" size="sm"
        footer={
          <>
            <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">Cancel</button>
            <button onClick={() => handleDelete(deleteConfirm)} className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600">Delete</button>
          </>
        }
      >
        <p className="text-slate-600 text-sm">Are you sure you want to delete this result? This action cannot be undone.</p>
      </Modal>
    </AdminLayout>
  );
};

export default AdminResults;
